import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IIntegrationResponse } from '@kcftech/prometheustypescriptapiclient';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { ArrowLink } from 'components/link/ArrowLink';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { IntegrationsPagedDataSource } from 'custom_hooks/data_hooks/data_source/integrationsDataSource';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { isKcfUser } from 'pages/Desk/issues/shared';
import { DeleteIntegrationCell } from 'pages/Settings/Integrations/DeleteIntegrationCell';
import { StatusLozenge } from 'pages/Settings/Integrations/StatusLozenge';
import { IntegrationState } from 'pages/Settings/Integrations/types';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ExternalLink } from 'routing/ExternalLink';
import { tokens } from 'styles/tokens';
import { getId } from 'utils/data';
import { getSettingsBaseRoute } from 'utils/RouteHelper';

export type IntegrationsTableProps = Readonly<{
    dataSource: IntegrationsPagedDataSource;
    locationId: string;
}>;

export function IntegrationsTable({ dataSource, locationId }: IntegrationsTableProps) {
    const { url } = useRouteMatch();
    const settingsRoute = getSettingsBaseRoute(url);
    const { reloadPage } = dataSource;
    const { data: userInfo } = useCurrentUserInfo();
    const isUserKcf = userInfo ? isKcfUser(userInfo.ApplicationRole) : false;

    const columns = useMemo<TableColumn<IIntegrationResponse>[]>(
        () => [
            {
                id: 'IntegrationType',
                header: () => 'Integration Type',
                value: ({ rowData }) => (
                    <ArrowLink
                        title="View Integration"
                        to={`${settingsRoute}/integrations/${rowData.Id}`}
                    >
                        {rowData.IntegrationType.toUpperCase()}
                    </ArrowLink>
                ),
                primary: true,
            },
            {
                id: 'SoftwareName',
                header: () => 'Software Name',
                value: ({ rowData }) => rowData.SoftwareName,
            },
            {
                id: 'CreatedDate',
                header: () => 'Created Date',
                value: ({ rowData }) => <DateRelativeToNow date={rowData.Created} />,
            },
            {
                id: 'Active',
                header: () => 'Status',
                value: ({ rowData }) => (
                    <StatusLozenge
                        status={
                            rowData.Active
                                ? IntegrationState.Active
                                : IntegrationState.Inactive
                        }
                        color={
                            rowData.Active
                                ? tokens.success.default
                                : tokens.text.secondary
                        }
                    />
                ),
            },
            {
                header: () => 'Actions',
                id: 'Actions',
                value: ({ rowData }) => (
                    <ActionMenuTableCell>
                        <DeleteIntegrationCell
                            locationId={locationId}
                            rowData={rowData}
                            onChange={reloadPage}
                        />
                    </ActionMenuTableCell>
                ),
            },
        ],
        [locationId, reloadPage, settingsRoute]
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            noResultMessage={
                isUserKcf ? (
                    'There are no integrations for your location.'
                ) : (
                    <>
                        There are no integrations for your location. To learn more about
                        enabling integrations, please contact your KCF sales
                        representative or{' '}
                        <ExternalLink url="https://support.kcftech.com">
                            <>
                                submit a support ticket{' '}
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            </>
                        </ExternalLink>
                    </>
                )
            }
            columns={columns}
            rowKey={getId}
        />
    );
}
